type RouterBasenameOptions = {
  /**
   * import.meta.env.BASE_URL — the subfolder this artifact was BUILT for (Vite
   * injects it from `--base`). "/" for the root builds (production, e2e),
   * "/<app>/" for the preview builds. Never stale: it is baked in per build.
   */
  baseUrl: string;
  /** window.location.pathname — where the artifact is ACTUALLY being served. */
  pathname: string;
  isDev: boolean;
};

/**
 * The router basename must equal the path where the SPA is actually mounted at
 * runtime. Two signals decide it, and both are needed:
 *   - `baseUrl` is the *candidate* mount — the subfolder the build was told it
 *     lives at (`--base`).
 *   - `pathname` is where we are *actually* served. The same preview artifact is
 *     served at "/<app>/" on the raw *.vercel.app URL but at "/" on the
 *     *-staging.dakotaprep.com aliases (host rewrites in vercel.json).
 * Use the subfolder only when the build has one AND we are actually under it;
 * otherwise "/". Sourcing this from `baseUrl` alone (the pre-2026-07 bug) blanked
 * the root-served staging aliases because `<BrowserRouter basename="/plumbing">`
 * matched nothing at "/". A hardcoded app→path map would only relocate that
 * staleness to a table you can forget to update when adding a variant.
 */
export function getRouterBasename({
  baseUrl,
  pathname,
  isDev,
}: RouterBasenameOptions): string {
  if (isDev) return "/";

  const mount = baseUrl.replace(/\/+$/, ""); // "/plumbing" | "" (root build)
  if (!mount) return "/";

  return pathname === mount || pathname.startsWith(`${mount}/`) ? mount : "/";
}
